<template>
  <div class="px-[24px] h-screen flex flex-col justify-center">
    <div class="">
      <img
        v-if="isKids"
        src="/images/findee-logo.png"
        class="mx-auto"
        alt="logo"
      />
      <FindeePetsLogo
        v-else-if="isPets"
        class="mx-auto"
      />
      <GuardianLogo v-else />

      <StrongTitle
        :class="{
          'mt-8': isKids || isPets,
          'mt-4': isGuardian
        }"
      >
        {{ $t('new_password_title') }}
      </StrongTitle>

      <div class="mt-5 text-center">
        {{ $t('new_password_title_text') }}
      </div>

      <PasswordInput
        v-model="state.password"
        v-model:valid="state.passwordValid"
      />
      <PasswordInput
        id="password_repeat"
        v-model="state.repeatPassword"
        v-model:valid="state.repeatPasswordValid"
        :validation-enabled="true"
      />

      <ErrorWidget
        v-if="hasError"
        role="alert"
      >
        {{ $t('error_general') }}
      </ErrorWidget>

      <BaseButton
        :loading="loading"
        class="mt-5"
        :disabled="!canResetPassword"
        @click="resetPasswordAction"
      >
        {{ $t('new_password_action') }}
      </BaseButton>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ROUTE_NAMES } from '@/router'
import BaseButton from '@/components/buttons/BaseButton.vue'
import StrongTitle from '@/components/ui/StrongTitle.vue'
import PasswordInput from '@/components/inputs/PasswordInput.vue'
import ErrorWidget from '@/components/ui/ErrorWidget.vue'
import { setNewPassword } from '@/services/api/Auth'
import { logAuthEvent } from '@/services/debug/authDebug'
import GuardianLogo from '@/assets/images/guardian/logo/LogoHorizontal.vue'
import FindeePetsLogo from '@/assets/images/pets/LogoHorizontal.vue'
import { isKids, isGuardian, isPets } from '@/comp/AppType'

const router = useRouter()
const route = useRoute()

const code = route.params.code

const state = reactive({
  password: '',
  repeatPassword: '',
  passwordValid: false,
  repeatPasswordValid: false
})

const passwordMatch = computed(() => state.password === state.repeatPassword)
const canResetPassword = computed(
  () => !loading.value && passwordMatch.value && state.passwordValid && state.repeatPasswordValid
)

const loading = ref(false)
const hasError = ref(false)
const resetPasswordAction = async () => {
  if (!canResetPassword.value) return
  loading.value = true
  hasError.value = false
  try {
    const result = await setNewPassword(code, { newPassword: state.password })
    if (
      !result ||
      result.error?.value ||
      result.statusCode?.value >= 400 ||
      result.data?.value?.success === false
    ) {
      hasError.value = true
      logAuthEvent(
        'auth.password-reset.submit.failed',
        {
          statusCode: result?.statusCode?.value ?? null,
          requestId: result?.response?.value?.headers?.get?.('x-correlation-id') || '',
          reason: 'request-failed'
        },
        'error'
      )
      return
    }
    await router.push({ name: ROUTE_NAMES.PASSWORD_RESET_SUCCESS })
  } catch (error) {
    hasError.value = true
    logAuthEvent(
      'auth.password-reset.submit.failed',
      { reason: 'unexpected-exception', errorName: error?.name || 'Error' },
      'error'
    )
  } finally {
    loading.value = false
  }
}
</script>
